import { NEXT_PUBLIC_BASE_URL } from '@env';

import type { ProxyLinkSource } from '.cache/__types__';

/** Options for generating card URLs */
export type GetCardUrlOptions = {
  /** Source identifier for proxy links */
  blinqSource?: ProxyLinkSource;
};

/** Variants for QR link subdomain experiments */
export type QrLinkSubdomainVariant =
  | 'control'
  | 'share'
  | 'share1'
  | 'share2'
  | 'share3'
  | 'fallback'
  | 'off';

export type GetQrCardUrlOptions = GetCardUrlOptions & {
  qrVariant?: QrLinkSubdomainVariant;
};

/** Subdomain prefixes for QR code URLs */
export enum Subdomain {
  Share = 's',
  Share1 = 's1',
  Share2 = 's2',
  Share3 = 's3',
  Share0 = 's0',
}

/**
 * Generates a standard card URL for the given card ID and options.
 * @param cardId - The card identifier to generate URL for
 * @param options - Additional options for URL generation
 * @returns The complete card URL
 */
export function getCardUrl(cardId?: string, options?: GetCardUrlOptions): string {
  const base = new URL(NEXT_PUBLIC_BASE_URL);
  const url = cardId ? new URL(cardId, base) : base;

  if (!cardId) {
    return url.toString();
  }

  if (options?.blinqSource) {
    url.searchParams.set('bs', String(options.blinqSource));
  }

  return url.toString();
}

function transformQrCardUrl(url: string, qrVariant?: QrLinkSubdomainVariant): string {
  const parsed = new URL(url);

  // Remove once confirmed with App Clip team
  parsed.searchParams.append('ida_v', 'treatment');
  parsed.host = getQrUrlHost(qrVariant);

  return parsed.toString();
}

function getQrUrlHost(variant?: QrLinkSubdomainVariant): string {
  const { host } = new URL(NEXT_PUBLIC_BASE_URL);

  switch (variant) {
    case 'control':
      return host;
    case 'share':
    case 'fallback':
    default:
      return `${Subdomain.Share}.${host}`;
    case 'share1':
      return `${Subdomain.Share1}.${host}`;
    case 'share2':
      return `${Subdomain.Share2}.${host}`;
    case 'share3':
      return `${Subdomain.Share3}.${host}`;
    case 'off':
      return `${Subdomain.Share0}.${host}`;
  }
}

export function getQrCardUrl(cardId?: string, options?: GetQrCardUrlOptions): string {
  const { qrVariant = 'share', ...cardUrlOptions } = options ?? {};

  return transformQrCardUrl(getCardUrl(cardId, cardUrlOptions), qrVariant);
}
